import { useAuth } from './AuthContext'
import { useMemo, useCallback } from 'react'

/**
 * 會員方案權限管理 Hook
 * 根據會員方案控制功能訪問權限
 */
export const useMembership = () => {
  const { user, businessPlan, permissionsLoaded } = useAuth()

  /**
   * 功能與方案的對應關係
   * 根據 MembershipInfoPage.js 中的功能列表定義
   */
  const featurePlanMap = useMemo(() => {
    return {
      // 薪水與業績管理 - 商務方案與品牌方案（與 MembershipInfoPage 承諾一致）
      salary: ['business', 'brand'],
      // 結帳 - 品牌方案與商務方案
      checkout: ['brand', 'business'],
      // 儲值金模組 - 品牌方案與商務方案
      storedValue: ['brand', 'business'],
      // 堂票模組 - 只在品牌方案
      classTicket: ['brand'],
      // 項目施作同意書 - 商務方案與品牌方案
      consentForm: ['business', 'brand'],
      // 顧客評價回覆 - 全方案開放（評價是店家形象的一部分，不當作加值功能賣）
      review: ['free', 'basic', 'business', 'brand'],
      // 施作照片紀錄 - 只在品牌方案
      //（對齊後端 servicePhotoRoutes: requireSubscription('brand')）
      servicePhoto: ['brand'],
      // 分期收款（店內自行分期）- 只在品牌方案
      //（對齊後端 installmentRoutes: requireSubscription('brand')）
      installment: ['brand'],
      // 紅利點數 - 商務方案與品牌方案
      //（對齊後端 pointsRoutes: requireSubscription('business')）
      points: ['business', 'brand'],
      // 庫存管理 - 品牌方案與商務方案
      inventory: ['brand', 'business'],
      // 招聘與媒合 - 只在品牌方案
      recruitment: ['brand'],
      // 平台推廣與品牌曝光 - 只在品牌方案
      promotion: ['brand'],
      // 專屬徵章與合作資格優先 - 只在品牌方案
      badge: ['brand'],
      // 營運報表與數據分析 - 商務方案和品牌方案
      analytics: ['business', 'brand'],
      // 團隊排班管理 - 兩方案制後開放給一般方案（basic），舊商務方案保留
      scheduling: ['basic', 'business', 'brand'],
      // 班表（月排班表 + 休假月曆）- 排班是開店的基本需求，所有方案都開放
      schedule: ['free', 'basic', 'business', 'brand'],
      // Google 預訂 - 商務方案和品牌方案
      googleBooking: ['business', 'brand'],
      // 排行榜參與資格 - 商務方案和品牌方案
      ranking: ['business', 'brand'],
      // 作品集展示 - 所有付費方案（basic, business, brand）
      portfolio: ['basic', 'business', 'brand'],
      // 回訪提醒功能 - 只在高階方案（對齊後端 businessRoutes: requireSubscription('brand')）
      revisitReminder: ['brand'],
      // Google 日曆同步 - 只在高階方案
      // （新增：原本 B 端與 App 端都缺這個 key，hasFeature 會回 false 並噴 console.warn）
      googleCalendar: ['brand'],
      // 自訂會員等級／群組 - 商務方案與品牌方案
      customMembership: ['business', 'brand'],
      // 線上商店下單模組 - 高階方案與舊商務方案
      // 門檻與既有 storeRoutes（商品／庫存）一致，避免出現
      // 「能建商品卻不能賣」的怪狀態
      shopOrder: ['business', 'brand'],
      // 不指定人員 - 幫店家多接單的功能，擋起來反而少賺，所有方案都開放
      unspecifiedStylist: ['free', 'basic', 'business', 'brand'],
      // 基本功能 - 所有方案都有
      basic: ['free', 'basic', 'business', 'brand']
    }
  }, [])

  /**
   * 檢查當前方案是否支持某個功能
   * @param {string} feature - 功能名稱
   * @returns {boolean}
   */
  const hasFeature = useCallback(
    (feature) => {
      if (!businessPlan) return false

      const allowedPlans = featurePlanMap[feature]
      if (!allowedPlans) {
        console.warn(`未知的功能: ${feature}`)
        return false
      }

      return allowedPlans.includes(businessPlan)
    },
    [businessPlan, featurePlanMap]
  )

  /**
   * 取得某功能「最低需要升級到哪個現行方案」，用於升級提示文案。
   * business（商務方案）已停售，不可作為新購或升級的引導對象，因此排除。
   * @param {string} feature
   * @returns {string} 'basic' | 'brand'
   */
  const getRequiredPlanForFeature = useCallback(
    (feature) => {
      const PLAN_RANK = { free: 0, basic: 1, brand: 2 }
      const plans = (featurePlanMap[feature] || []).filter(
        (p) => p !== 'business'
      )
      return plans.sort((a, b) => PLAN_RANK[a] - PLAN_RANK[b])[0] || 'brand'
    },
    [featurePlanMap]
  )

  /**
   * 檢查是否為特定方案
   * @param {string|string[]} plans - 方案名稱或方案數組
   * @returns {boolean}
   */
  const isPlan = useCallback(
    (plans) => {
      if (!businessPlan) return false

      const planArray = Array.isArray(plans) ? plans : [plans]
      return planArray.includes(businessPlan)
    },
    [businessPlan]
  )

  /**
   * 檢查是否為付費方案
   * @returns {boolean}
   */
  const isPaidPlan = useCallback(() => {
    return isPlan(['basic', 'business', 'brand'])
  }, [isPlan])

  /**
   * 檢查是否為品牌方案
   * @returns {boolean}
   */
  const isBrandPlan = useCallback(() => {
    return isPlan('brand')
  }, [isPlan])

  /**
   * 檢查是否為商務方案或品牌方案
   * @returns {boolean}
   */
  const isBusinessOrBrand = useCallback(() => {
    return isPlan(['business', 'brand'])
  }, [isPlan])

  /**
   * 各方案的數量上限
   * 注意：
   * - 全部同步自後端 shared/config/planLimits.js（唯一資料來源），
   *   員工上限為 free=2、basic=2、business=3、brand=6，請在兩端保持一致。
   * - 這裡只是前端顯示用，實際擋人的是後端 utils/planEntitlements.js。
   * - 個別店家可能有 planOverrides.employeeLimit 豁免（舊上限保留），
   *   前端拿不到該欄位時仍顯示方案預設值，實際新增時以後端回傳為準。
   */
  const PLAN_LIMITS = useMemo(
    () => ({
      free: { employees: 2, productCategories: 1, products: 1 },
      basic: { employees: 2, productCategories: 10, products: 50 },
      business: { employees: 3, productCategories: 30, products: 100 },
      brand: { employees: 6, productCategories: 50, products: 200 }
    }),
    []
  )

  /**
   * 取得當前方案某項數量上限
   * @param {string} key  employees | productCategories | products
   * @returns {number}    上限值
   */
  const getPlanLimit = useCallback(
    (key) => {
      const cfg = PLAN_LIMITS[businessPlan] || PLAN_LIMITS.free
      const v = cfg[key]
      return typeof v === 'number' ? v : 0
    },
    [businessPlan, PLAN_LIMITS]
  )

  return {
    plan: businessPlan,
    hasFeature,
    getRequiredPlanForFeature,
    isPlan,
    isPaidPlan,
    isBrandPlan,
    isBusinessOrBrand,
    permissionsLoaded,
    getPlanLimit,
    PLAN_LIMITS
  }
}
